import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import type { AppointmentsBarPoint } from "@/hooks/useReportsOverview";
import { CardHeader, CardTitle, CardDescription, CardContent, Card } from "@/components/ui/card";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";

interface AppointmentsDailyVolumeChartProps {
  currentData: AppointmentsBarPoint[];
  comparisonData: AppointmentsBarPoint[];
  isLoading: boolean;
  currentLabel: string;
  comparisonLabel: string;
}

interface MergedPoint {
  label: string;
  current: number;
  comparison: number | null;
}

export function AppointmentsDailyVolumeChart({
  currentData,
  comparisonData,
  isLoading,
  currentLabel,
  comparisonLabel,
}: AppointmentsDailyVolumeChartProps) {
  const { t, i18n } = useTranslation("reports");
  const locale = i18n.language === "pt" ? ptBR : enUS;

  const merged: MergedPoint[] = currentData.map((pt, i) => ({
    label: (() => {
      try {
        return format(new Date(pt.period), "dd/MM", { locale });
      } catch {
        return pt.label ?? pt.period;
      }
    })(),
    current: pt.agendados,
    comparison: comparisonData[i]?.agendados ?? null,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("dailyVolumeTitle")}</CardTitle>
        <CardDescription>{t("dailyVolumeDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-lg" />
        ) : merged.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
            {t("emptyBreakdown")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={merged} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickLine={false}
                axisLine={false}
              />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend iconType="line" iconSize={12} wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="current"
                name={currentLabel}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              {comparisonData.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="comparison"
                  name={comparisonLabel}
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
